import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type {
  IApiGeneralParams,
  IApiPaginatedResponse,
  IApiResponse,
} from "@/types";
import type {
  IPerformanceMetric,
  IPerformanceMetricPayload,
  IPerformanceMetricStatusPayload,
  ITeamPerformance,
  ITeamMember,
  IPerformanceCycle,
  IEvaluator,
  IPerformanceReview,
  IPerformanceConfiguration,
  IPerformanceConfigurationPayload,
  IPerformanceConfigurationDetail,
  IPerformanceTeamStatistics,
  IPerformanceTeamEvaluationStats,
  IPerformanceTeamMemberEvaluationStats,
  IEvaluationCycle,
  IEvaluationRequest,
  IEvaluationStatistics,
  IEvaluationRequestScorePayload,
  IEvaluationCycleScoreItem,
  IEvaluationRequestDetail,
} from "@/types/(company)/performance.interface";
import { createApi } from "@reduxjs/toolkit/query/react";

export const performanceApi = createApi({
  reducerPath: "performanceApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "GetAllPerformanceMetrics",
    "GetPerformanceMetricById",
    "GetPaginatedPerformanceMetrics",
    "GetTeamPerformance",
    "GetTeamMembers",
    "GetPerformanceCycles",
    "GetEvaluators",
    "GetPerformanceReview",
    "GetPerformanceConfiguration",
    "GetTeamStatistics",
    "GetTeamEvaluationStats",
    "GetTeamMemberEvaluationStats",
    "GetEvaluationCycles",
    "GetEvaluationCyclesScoreById",
    "GetEvaluationRequests",
    "GetEvaluationRequestDetailsById",
    "GetEvaluationStatistics",
  ],
  endpoints: (builder) => ({
    // ─── Metrics ────────────────────────────────────────────────

    getAllPerformanceMetrics: builder.query<
      IApiResponse<IPerformanceMetric[]>,
      void
    >({
      query: () => ({ url: APIEndpoints.Company.settings.performanceMetrics }),
      providesTags: ["GetAllPerformanceMetrics"],
    }),

    createPerformanceMetric: builder.mutation<
      IApiResponse<void>,
      IPerformanceMetricPayload
    >({
      query: (payload) => ({
        url: APIEndpoints.Company.settings.performanceMetrics,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [
        "GetAllPerformanceMetrics",
        "GetPaginatedPerformanceMetrics",
      ],
    }),

    getPerformanceMetricById: builder.query<
      IApiResponse<IPerformanceMetric>,
      { metricId: string }
    >({
      query: ({ metricId }) => ({
        url: APIEndpoints.Company.settings.performanceMetricsById(metricId),
      }),
      providesTags: ["GetPerformanceMetricById"],
    }),

    updatePerformanceMetric: builder.mutation<
      IApiResponse<void>,
      { metricId: string; payload: IPerformanceMetricPayload }
    >({
      query: ({ metricId, payload }) => ({
        url: APIEndpoints.Company.settings.performanceMetricsById(metricId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: [
        "GetAllPerformanceMetrics",
        "GetPerformanceMetricById",
        "GetPaginatedPerformanceMetrics",
      ],
    }),

    deletePerformanceMetric: builder.mutation<
      IApiResponse<void>,
      { metricId: string }
    >({
      query: ({ metricId }) => ({
        url: APIEndpoints.Company.settings.performanceMetricsById(metricId),
        method: "DELETE",
      }),
      invalidatesTags: [
        "GetAllPerformanceMetrics",
        "GetPaginatedPerformanceMetrics",
      ],
    }),

    getPaginatedPerformanceMetrics: builder.query<
      IApiPaginatedResponse<IPerformanceMetric[]>,
      IApiGeneralParams
    >({
      query: (params) => ({
        url: APIEndpoints.Company.settings.performanceMetricsPaginated,
        params,
      }),
      providesTags: ["GetPaginatedPerformanceMetrics"],
    }),

    getPerformanceMetrics: builder.infiniteQuery<
      IApiPaginatedResponse<IPerformanceMetric[]>,
      { search?: string },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (result) => {
          if (result.meta.currentPage < result.meta.totalPages) {
            return result.meta.currentPage + 1;
          }
          return undefined;
        },
      },
      query: ({ queryArg: { search }, pageParam }) => ({
        url: APIEndpoints.Company.settings.performanceMetricsPaginated,
        params: { search, page: pageParam, limit: 10, isActive: true },
      }),
    }),

    updatePerformanceMetricStatus: builder.mutation<
      IApiResponse<void>,
      { metricId: string; payload: IPerformanceMetricStatusPayload }
    >({
      query: ({ metricId, payload }) => ({
        url: APIEndpoints.Company.settings.updatePerformanceMetricStatus(
          metricId,
        ),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: [
        "GetAllPerformanceMetrics",
        "GetPerformanceMetricById",
        "GetPaginatedPerformanceMetrics",
      ],
    }),

    // ─── Teams ──────────────────────────────────────────────────

    getTeamPerformance: builder.query<
      IApiResponse<ITeamPerformance[]>,
      IApiGeneralParams
    >({
      query: (params) => ({
        url: APIEndpoints.Company.performance.getTeamPerformance,
        params,
      }),
      providesTags: ["GetTeamPerformance"],
    }),

    getTeamMembersByTeamId: builder.query<
      IApiResponse<ITeamMember[]>,
      { teamId: string } & IApiGeneralParams
    >({
      query: ({ teamId, ...params }) => ({
        url: APIEndpoints.Company.performance.getTeamMembersByTeamId(teamId),
        params,
      }),
      providesTags: ["GetTeamMembers"],
    }),

    // ─── Members ────────────────────────────────────────────────

    getPerformanceCyclesByMemberId: builder.query<
      IApiResponse<IPerformanceCycle[]>,
      { memberId: string } & IApiGeneralParams
    >({
      query: ({ memberId, ...params }) => ({
        url: APIEndpoints.Company.performance.getPerformanceCyclesbyMemberId(
          memberId,
        ),
        params,
      }),
      providesTags: ["GetPerformanceCycles"],
    }),

    // ─── Evaluations ────────────────────────────────────────────

    getEvaluatorsByEvaluationId: builder.query<
      IApiResponse<IEvaluator[]>,
      { evaluationId: string } & IApiGeneralParams
    >({
      query: ({ evaluationId, ...params }) => ({
        url: APIEndpoints.Company.performance.getEvaluatorsbyEvaluationId(
          evaluationId,
        ),
        params,
      }),
      providesTags: ["GetEvaluators"],
    }),

    getPerformanceReviewById: builder.query<
      IApiResponse<IPerformanceReview>,
      { id: string }
    >({
      query: ({ id }) => ({
        url: APIEndpoints.Company.performance.getPerformanceReviewById(id),
      }),
      providesTags: ["GetPerformanceReview"],
    }),

    // ─── Configuration ──────────────────────────────────────────

    getPerformanceConfiguration: builder.query<
      IApiResponse<IPerformanceConfiguration[]>,
      { teamId: string }
    >({
      query: ({ teamId }) => ({
        url: APIEndpoints.Company.performance.performanceConfiguration(teamId),
      }),
      providesTags: ["GetPerformanceConfiguration"],
    }),

    createPerformanceConfiguration: builder.mutation<
      IApiResponse<void>,
      { teamId: string; payload: IPerformanceConfigurationPayload }
    >({
      query: ({ teamId, payload }) => ({
        url: APIEndpoints.Company.performance.performanceConfiguration(teamId),
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["GetPerformanceConfiguration"],
    }),

    updatePerformanceConfiguration: builder.mutation<
      IApiResponse<void>,
      { configId: string; payload: IPerformanceConfigurationPayload }
    >({
      query: ({ configId, payload }) => ({
        url: APIEndpoints.Company.performance.performanceConfigurationById(
          configId,
        ),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["GetPerformanceConfiguration"],
    }),

    getPerformanceConfigurationById: builder.query<
      IApiResponse<IPerformanceConfigurationDetail>,
      { id: string }
    >({
      query: ({ id }) => ({
        url: APIEndpoints.Company.performance.performanceConfigurationById(id),
      }),
      providesTags: ["GetPerformanceConfiguration"],
    }),
    getTeamStatistics: builder.query<
      IApiResponse<IPerformanceTeamStatistics>,
      void
    >({
      query: () => ({
        url: APIEndpoints.Company.performance.getTeamStatistics,
      }),
      providesTags: ["GetTeamStatistics"],
    }),
    getTeamEvaluationStats: builder.query<
      IApiResponse<IPerformanceTeamEvaluationStats>,
      { teamId: string }
    >({
      query: ({ teamId }) => ({
        url: APIEndpoints.Company.performance.getTeamEvalutaionStats(teamId),
      }),
      providesTags: ["GetTeamEvaluationStats"],
    }),
    getTeamMemberEvaluationStats: builder.query<
      IApiResponse<IPerformanceTeamMemberEvaluationStats>,
      { memberId: string }
    >({
      query: ({ memberId }) => ({
        url: APIEndpoints.Company.performance.getTeamMemberEvalutaionStats(
          memberId,
        ),
      }),
      providesTags: ["GetTeamMemberEvaluationStats"],
    }),
    // ─── My Space ───────────────────────────────────────────────

    getEvaluationCycles: builder.query<
      IApiResponse<IEvaluationCycle[]>,
      IApiGeneralParams
    >({
      query: (params) => ({
        url: APIEndpoints.MySpace.performance.getEvalutaionCycles,
        params,
      }),
      providesTags: ["GetEvaluationCycles"],
    }),

    getEvaluationCyclesScoreById: builder.query<
      IApiResponse<IEvaluationCycleScoreItem[]>,
      { cyclesId: number } & IApiGeneralParams
    >({
      query: ({ cyclesId, ...params }) => ({
        url: APIEndpoints.MySpace.performance.evaluationCyclesScoreById(
          cyclesId,
        ),
        params,
      }),
      providesTags: ["GetEvaluationCyclesScoreById"],
    }),

    getEvaluationRequests: builder.query<
      IApiResponse<IEvaluationRequest[]>,
      IApiGeneralParams
    >({
      query: (params) => ({
        url: APIEndpoints.MySpace.performance.getEvalutaionRequest,
        params,
      }),
      providesTags: ["GetEvaluationRequests"],
    }),

    getEvaluationRequestDetailsById: builder.query<
      IApiResponse<IEvaluationRequestDetail>,
      { requestId: number }
    >({
      query: ({ requestId }) => ({
        url: APIEndpoints.MySpace.performance.getevaluatonRequestDetailsById(
          requestId,
        ),
      }),
      providesTags: ["GetEvaluationRequestDetailsById"],
    }),

    updateEvaluationRequestDetailsById: builder.mutation<
      IApiResponse<void>,
      { requestId: number; payload: IEvaluationRequestScorePayload[] }
    >({
      query: ({ requestId, payload }) => ({
        url: APIEndpoints.MySpace.performance.evaluationCyclesScoreById(
          requestId,
        ),
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: [
        "GetEvaluationRequestDetailsById",
        "GetEvaluationRequests",
      ],
    }),
    getEvaluationStatistics: builder.query<
      IApiResponse<IEvaluationStatistics>,
      void
    >({
      query: () => ({
        url: APIEndpoints.MySpace.performance.getEvaluationStatistics,
      }),
      providesTags: ["GetEvaluationStatistics"],
    }),
  }),
});

export const {
  // Metrics
  useGetAllPerformanceMetricsQuery,
  useCreatePerformanceMetricMutation,
  useGetPerformanceMetricByIdQuery,
  useUpdatePerformanceMetricMutation,
  useDeletePerformanceMetricMutation,
  useGetPaginatedPerformanceMetricsQuery,
  useGetPerformanceMetricsInfiniteQuery,
  useUpdatePerformanceMetricStatusMutation,
  // Teams
  useGetTeamPerformanceQuery,
  useGetTeamMembersByTeamIdQuery,
  // Members
  useGetPerformanceCyclesByMemberIdQuery,
  // Evaluations
  useGetEvaluatorsByEvaluationIdQuery,
  useGetPerformanceReviewByIdQuery,
  // Configuration
  useGetPerformanceConfigurationQuery,
  useCreatePerformanceConfigurationMutation,
  useUpdatePerformanceConfigurationMutation,
  useGetPerformanceConfigurationByIdQuery,
  // stats
  useGetTeamStatisticsQuery,
  useGetTeamEvaluationStatsQuery,
  useGetTeamMemberEvaluationStatsQuery,
  //My-space
  useGetEvaluationStatisticsQuery,
  useGetEvaluationCyclesQuery,
  useGetEvaluationCyclesScoreByIdQuery,
  useGetEvaluationRequestsQuery,
  useGetEvaluationRequestDetailsByIdQuery,
  useUpdateEvaluationRequestDetailsByIdMutation,
} = performanceApi;
